import { useEffect, useState, type ChangeEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { apiErrorMessage } from '../../utils/errors';
import {
  useInviteInfo,
  useUploadInviteDoc,
  useAcceptInvite,
  useDownloadInviteAgreement,
} from '../../features/customers/api';
import './InviteLandingPage.scss';

type InviteDocumentType = 'AGREEMENT' | 'RESALE_CERTIFICATE';

export default function InviteLandingPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const auth = getAuth();

  const { data, isLoading, isError, refetch } = useInviteInfo(token ?? '', true);
  const upload = useUploadInviteDoc(token ?? '');
  const accept = useAcceptInvite(token ?? '');
  const dl = useDownloadInviteAgreement(token);

  const [authReady, setAuthReady] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [switchingAccount, setSwitchingAccount] = useState(false);
  const [agreementUploaded, setAgreementUploaded] = useState(false);
  const [agreementName, setAgreementName] = useState('');
  const [resaleUploaded, setResaleUploaded] = useState(false);
  const [resaleName, setResaleName] = useState('');
  const [uploadingType, setUploadingType] = useState<InviteDocumentType | null>(null);

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      setIsAuthed(Boolean(user));
      setUserEmail(user?.email ?? '');
      setAuthReady(true);
    });
  }, [auth]);

  useEffect(() => {
    if (!data) return;
    setAgreementUploaded(Boolean(data.uploaded?.AGREEMENT));
    setResaleUploaded(Boolean(data.uploaded?.RESALE_CERTIFICATE));
  }, [data]);

  const invitedEmail = (data?.email ?? '').trim().toLowerCase();
  const emailMatches = Boolean(invitedEmail && userEmail.trim().toLowerCase() === invitedEmail);
  const canUpload = authReady && isAuthed && emailMatches;

  // Preserve the server's existing misspelled property names.
  const needsAgreement = Boolean(data?.isAgreementRequered);
  const needsResale = Boolean(data?.isResaleCertificateRequered);
  const hasAllRequired = (!needsAgreement || agreementUploaded) && (!needsResale || resaleUploaded);
  const isBusy = Boolean(uploadingType || upload.isPending || accept.isPending);
  const canSubmit = canUpload && hasAllRequired && !isBusy;

  const redirect = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
  const signUpUrl = `/signup?redirect=${redirect}`;
  const signInUrl = `/login?redirect=${redirect}`;

  const handleSwitchAccount = async () => {
    try {
      setSwitchingAccount(true);
      await signOut(auth);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Could not switch accounts. Please try again.'));
    } finally {
      setSwitchingAccount(false);
    }
  };

  const handleUpload = async (type: InviteDocumentType, event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file) return;

    // Some browsers leave File.type empty. Never trust the extension alone on the server.
    const isPdf = /\.pdf$/i.test(file.name) && (!file.type || file.type === 'application/pdf');
    if (!isPdf) {
      toast.error('Please choose a PDF file.');
      input.value = '';
      return;
    }

    // Clearing permits choosing the same PDF again after an upload failure.
    input.value = '';
    try {
      setUploadingType(type);
      await upload.mutateAsync({ type, file });
      if (type === 'AGREEMENT') {
        setAgreementUploaded(true);
        setAgreementName(file.name);
      } else {
        setResaleUploaded(true);
        setResaleName(file.name);
      }
      toast.success(type === 'AGREEMENT' ? 'Agreement uploaded.' : 'Resale certificate uploaded.');
      void refetch();
    } catch (error) {
      console.error(error);
      toast.error(apiErrorMessage(error, 'Could not upload the document.'));
    } finally {
      setUploadingType(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await dl.mutateAsync();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Agreement_Template.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      toast.error(apiErrorMessage(error, 'Unable to download the agreement template.'));
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await accept.mutateAsync();
      toast.success("Submitted. We'll review your documents.");
      nav('/settings/profile');
    } catch (error) {
      console.error(error);
      toast.error(apiErrorMessage(error, 'Failed to submit.'));
    }
  };

  if (isLoading || !authReady) {
    return (
      <main className="invite-page">
        <section className="invite-shell" aria-busy="true" aria-label="Loading invitation">
          <div className="invite-skeleton invite-skeleton--title" />
          <div className="invite-skeleton invite-skeleton--line" />
          <div className="invite-skeleton invite-skeleton--block" />
        </section>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="invite-page">
        <section className="invite-shell invite-shell--empty">
          <h1>Invitation unavailable</h1>
          <p>
            This invitation link is invalid or has expired. Ask the organization to send a new one.
          </p>
        </section>
      </main>
    );
  }

  // Optional, server-computed field from your invite-info API.
  // undefined means the backend hasn't been updated yet; show both choices safely.
  const hasAccount = (data as typeof data & { hasAccount?: boolean }).hasAccount;
  const needsAuth = !isAuthed;
  const wrongAccount = isAuthed && !emailMatches;
  const requiredCount = Number(needsAgreement) + Number(needsResale);
  const uploadedRequiredCount =
    Number(needsAgreement && agreementUploaded) + Number(needsResale && resaleUploaded);

  return (
    <main className="invite-page">
      <section className="invite-shell">
        <header className="invite-header">
          <div className="invite-eyebrow">CUSTOMER INVITATION</div>
          <h1>You're invited to {data.orgName}</h1>
          <p className="invite-subtitle">
            Finish setting up your access and submit any required documents.
          </p>
          <div className="invite-recipient">
            <span className="invite-recipient__label">Invitation sent to</span>
            <strong>{data.email}</strong>
          </div>
        </header>

        <section
          className={`invite-auth ${canUpload ? 'invite-auth--success' : wrongAccount ? 'invite-auth--warning' : ''}`}
          aria-label="Account access"
        >
          <div className="invite-section-heading">
            <span className="invite-step">1</span>
            <div>
              <h2>Access your account</h2>
              <p>Use the email address shown on your invitation.</p>
            </div>
          </div>

          {canUpload ? (
            <div className="invite-auth-message invite-auth-message--success" role="status">
              <span className="invite-state-icon" aria-hidden="true">
                ✓
              </span>
              <div>
                <strong>You're signed in</strong>
                <p>Signed in as {userEmail}. You can continue to your documents.</p>
              </div>
            </div>
          ) : wrongAccount ? (
            <div className="invite-auth-message invite-auth-message--warning">
              <span className="invite-state-icon" aria-hidden="true">
                !
              </span>
              <div>
                <strong>You're signed in with a different email</strong>
                <p>
                  You're using <strong>{userEmail || 'another account'}</strong>, but this
                  invitation belongs to <strong> {data.email}</strong>. Switch accounts to continue.
                </p>
                <button
                  type="button"
                  className="invite-button invite-button--primary invite-button--inline"
                  onClick={handleSwitchAccount}
                  disabled={switchingAccount || isBusy}
                >
                  {switchingAccount ? 'Switching…' : 'Switch account'}
                </button>
              </div>
            </div>
          ) : needsAuth ? (
            <div className="invite-auth-choice">
              {hasAccount === true ? (
                <>
                  <p>
                    You already have an account associated with this invitation. Sign in to
                    continue.
                  </p>
                  <Link className="invite-button invite-button--primary" to={signInUrl}>
                    Sign in to continue <span aria-hidden="true">→</span>
                  </Link>
                </>
              ) : hasAccount === false ? (
                <>
                  <p>
                    Welcome! You don't have an account yet. Create one using{' '}
                    <strong>{data.email}</strong> to get started.
                  </p>
                  <Link className="invite-button invite-button--primary" to={signUpUrl}>
                    Create your account <span aria-hidden="true">→</span>
                  </Link>
                </>
              ) : (
                <>
                  <p>
                    New here? Create an account. Already have one? Sign in. Be sure to use{' '}
                    <strong>{data.email}</strong>.
                  </p>
                  <div className="invite-button-row">
                    <Link className="invite-button invite-button--primary" to={signUpUrl}>
                      Create account
                    </Link>
                    <Link className="invite-button invite-button--secondary" to={signInUrl}>
                      Sign in
                    </Link>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </section>

        <section className="invite-documents" aria-label="Documents">
          <div className="invite-section-heading">
            <span className="invite-step">2</span>
            <div>
              <h2>Upload your documents</h2>
              <p>
                {requiredCount > 0
                  ? `${uploadedRequiredCount} of ${requiredCount} required ${requiredCount === 1 ? 'document' : 'documents'} uploaded`
                  : 'No documents are required, but you can upload them if needed.'}
              </p>
            </div>
          </div>

          <div className="invite-template">
            <div>
              <strong>Need an agreement form?</strong>
              <p>Download the PDF template, complete it, and upload the signed copy below.</p>
            </div>
            <button
              type="button"
              className="invite-button invite-button--secondary invite-button--small"
              onClick={handleDownloadTemplate}
              disabled={dl.isPending}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 3v12m0 0 4-4m-4 4-4-4M4 17v3h16v-3" />
              </svg>
              {dl.isPending ? 'Downloading…' : 'Download template'}
            </button>
          </div>

          <div className="invite-upload-grid">
            <div
              className={`invite-upload ${agreementUploaded ? 'invite-upload--complete' : ''} ${!canUpload ? 'invite-upload--disabled' : ''}`}
            >
              <div className="invite-upload__top">
                <h3>Agreement</h3>
                <span className={`invite-tag ${needsAgreement ? 'invite-tag--required' : ''}`}>
                  {needsAgreement ? 'Required' : 'Optional'}
                </span>
              </div>
              <svg
                className="invite-upload__icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6M8 13h8M8 17h8" />
              </svg>
              <p className="invite-upload__description">
                Upload your completed agreement as a PDF.
              </p>
              <input
                id="invite-agreement"
                className="invite-file-input"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) => void handleUpload('AGREEMENT', event)}
                disabled={!canUpload || isBusy}
              />
              <label
                className="invite-button invite-button--upload"
                htmlFor="invite-agreement"
                aria-disabled={!canUpload || isBusy}
              >
                {uploadingType === 'AGREEMENT'
                  ? 'Uploading…'
                  : agreementUploaded
                    ? 'Replace PDF'
                    : 'Choose PDF'}
              </label>
              <div
                className={`invite-upload__status ${agreementUploaded ? 'invite-upload__status--complete' : ''}`}
                aria-live="polite"
                title={agreementName || undefined}
              >
                {uploadingType === 'AGREEMENT'
                  ? 'Uploading your agreement…'
                  : agreementUploaded
                    ? `✓ Uploaded${agreementName ? `: ${agreementName}` : ''}`
                    : !canUpload
                      ? 'Sign in with the invited email to upload'
                      : 'No file uploaded'}
              </div>
            </div>

            <div
              className={`invite-upload ${resaleUploaded ? 'invite-upload--complete' : ''} ${!canUpload ? 'invite-upload--disabled' : ''}`}
            >
              <div className="invite-upload__top">
                <h3>Resale certificate</h3>
                <span className={`invite-tag ${needsResale ? 'invite-tag--required' : ''}`}>
                  {needsResale ? 'Required' : 'Optional'}
                </span>
              </div>
              <svg
                className="invite-upload__icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6M8 13h8M8 17h6" />
              </svg>
              <p className="invite-upload__description">Upload your resale certificate as a PDF.</p>
              <input
                id="invite-resale-certificate"
                className="invite-file-input"
                type="file"
                accept="application/pdf,.pdf"
                onChange={(event) => void handleUpload('RESALE_CERTIFICATE', event)}
                disabled={!canUpload || isBusy}
              />
              <label
                className="invite-button invite-button--upload"
                htmlFor="invite-resale-certificate"
                aria-disabled={!canUpload || isBusy}
              >
                {uploadingType === 'RESALE_CERTIFICATE'
                  ? 'Uploading…'
                  : resaleUploaded
                    ? 'Replace PDF'
                    : 'Choose PDF'}
              </label>
              <div
                className={`invite-upload__status ${resaleUploaded ? 'invite-upload__status--complete' : ''}`}
                aria-live="polite"
                title={resaleName || undefined}
              >
                {uploadingType === 'RESALE_CERTIFICATE'
                  ? 'Uploading your resale certificate…'
                  : resaleUploaded
                    ? `✓ Uploaded${resaleName ? `: ${resaleName}` : ''}`
                    : !canUpload
                      ? 'Sign in with the invited email to upload'
                      : 'No file uploaded'}
              </div>
            </div>
          </div>
          <p className="invite-help-text">
            PDF files only. Optional documents can be uploaded too.
          </p>
        </section>

        <footer className="invite-footer">
          <div className="invite-footer__copy">
            <strong>Ready to submit?</strong>
            <p>
              {!canUpload
                ? 'Sign in with your invited email to continue.'
                : !hasAllRequired
                  ? 'Upload all required documents before submitting.'
                  : 'Everything is ready for review.'}
            </p>
          </div>
          <button
            type="button"
            className="invite-button invite-button--primary invite-button--submit"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {accept.isPending ? 'Submitting…' : 'Submit for review'}
          </button>
        </footer>
      </section>
    </main>
  );
}
